import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import A_SimpleDialog from "../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import { fetchExposureGroupById } from "./EG.helper";
import { httpPatch, httpPost } from "../../../../../Utils/apis/apis";
import { setToast } from "../../../../../store/actions/toastActions";
import { toastSeverity } from "../../../../../Utils/config/toastConfig";
import { STRINGS, RISK_GRADE_MAP } from "./EG.constants";

const useStyles = makeStyles((theme) => ({
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
    color: "var(--color-primary-main)",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    "&:first-child": {
      marginTop: 0,
    },
  },
  sectionCard: {
    padding: 0,
    backgroundColor: "transparent",
  },
  field: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      fontSize: 13,
      backgroundColor: "var(--color-background-default)",
      "& fieldset": {
        borderColor: "var(--total_row_boldbackground)",
      },
      "&:hover fieldset": {
        borderColor: "var(--color-primary-main)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "var(--color-primary-main)",
        borderWidth: 1.5,
      },
      "&.Mui-disabled": {
        backgroundColor: "var(--total_row_background)",
        "& fieldset": {
          borderColor: "var(--total_row_boldbackground)",
          borderStyle: "dashed",
        },
        "& .MuiOutlinedInput-input": {
          color: "var(--color-text-secondary)",
          WebkitTextFillColor: "var(--color-text-secondary)",
        },
      },
    },
    "& .MuiInputLabel-outlined": {
      fontSize: 13,
      color: "var(--color-text-secondary)",
      transform: "translate(14px, 10px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
      },
      "&.Mui-disabled": {
        color: "var(--color-text-secondary)",
        opacity: 0.7,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      color: "var(--color-text-primary)",
    },
    "& .MuiOutlinedInput-inputMultiline": {
      resize: "vertical",
    },
    "& .MuiFormHelperText-root": {
      fontSize: 11,
      marginLeft: 2,
    },
  },
  riskLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--color-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: theme.spacing(0.75),
  },
  riskTiles: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  riskTile: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 12px",
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    border: "1.5px solid transparent",
    transition: "all 0.15s ease",
    userSelect: "none",
    "&:focus": {
      outline: "none",
    },
  },
  embeddedActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
}));

const EMPTY = {
  name: "",
  description: "",
  notes: "",
  accountBalanceOutsideDF: "",
  existingLimitOutsideDF: "",
  proposedLimitOutsideDF: "",
  internalrisk: "",
};

function EGEditDialog({ groupId, groupName, open, onClose, onSaved, isEdit, embedded }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFormData(EMPTY);
    setErrors({});
    if (!isEdit || !groupId) return;

    let cancelled = false;
    setIsLoading(true);
    fetchExposureGroupById(groupId)
      .then((data) => {
        if (cancelled) return;
        setFormData({
          description:             data.description || "",
          notes:                   data.notes || "",
          accountBalanceOutsideDF: data.accountBalanceOutsideDF ?? "",
          existingLimitOutsideDF:  data.existingLimitOutsideDF ?? "",
          proposedLimitOutsideDF:  data.proposedLimitOutsideDF ?? "",
          internalrisk:            data.internalrisk || data.internalRiskGrade || "",
        });
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [open, groupId, isEdit]);

  const handleChange = useCallback((e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) setErrors((prev) => ({ ...prev, [id]: "" }));
  }, [errors]);

  const handleSave = useCallback(async () => {
    const newErrors = {};
    if (!isEdit && !formData.name.trim()) newErrors.name = "Group name is required.";
    if (!formData.description.trim()) newErrors.description = "Description is required.";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...(!isEdit && { name: formData.name }),
        description:             formData.description,
        notes:                   formData.notes,
        accountBalanceOutsideDF: formData.accountBalanceOutsideDF,
        existingLimitOutsideDF:  formData.existingLimitOutsideDF,
        proposedLimitOutsideDF:  formData.proposedLimitOutsideDF,
        internalrisk:            formData.internalrisk,
      };

      let resultId = groupId;
      if (isEdit) {
        await httpPatch(`/ExposureGroup/updategroup/${groupId}`, payload);
        dispatch(setToast({ type: toastSeverity.SUCCESS, message: STRINGS.EDIT_SUCCESS, open: true }));
      } else {
        const result = await httpPost(`/ExposureGroup/addgroup`, payload);
        resultId = result?.id || result?.Id || null;
        dispatch(setToast({ type: toastSeverity.SUCCESS, message: STRINGS.ADD_SUCCESS, open: true }));
      }

      if (onSaved) onSaved(resultId);
      if (!embedded) onClose();
    } catch (_) {
      dispatch(setToast({
        type: toastSeverity.ERROR,
        message: isEdit ? STRINGS.EDIT_ERROR : STRINGS.ADD_ERROR,
        open: true,
      }));
    } finally {
      setIsSaving(false);
    }
  }, [groupId, formData, errors, dispatch, onClose, onSaved, isEdit, embedded]);

  const isDisabled = isLoading || isSaving;
  const dialogTitle = isEdit ? STRINGS.EDIT_TITLE : STRINGS.ADD_TITLE;
  const saveLabel = isSaving ? "Saving…" : isEdit ? STRINGS.EDIT_SAVE : STRINGS.ADD_SAVE;

  const formContent = isLoading ? (
    <Box style={{ display: "flex", justifyContent: "center", padding: 40 }}>
      <CircularProgress size={32} />
    </Box>
  ) : (
    <Box>
      <Typography className={classes.sectionLabel}>Group</Typography>
      <Box className={classes.sectionCard}>
        <TextField
          id="name"
          label="Group Name"
          variant="outlined"
          size="small"
          fullWidth
          required={!isEdit}
          value={isEdit ? (groupName || "") : formData.name}
          onChange={isEdit ? undefined : handleChange}
          disabled={isEdit || isSaving}
          error={!isEdit && Boolean(errors.name)}
          helperText={!isEdit ? errors.name : undefined}
          className={classes.field}
        />
      </Box>

      {/* Description & Notes — always visible */}
      <Typography className={classes.sectionLabel}>Content</Typography>
      <Box className={classes.sectionCard}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              id="description"
              label={STRINGS.FIELD_DESCRIPTION}
              variant="outlined"
              size="small"
              fullWidth
              required
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange}
              disabled={isSaving}
              error={Boolean(errors.description)}
              helperText={errors.description}
              className={classes.field}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="notes"
              label={STRINGS.FIELD_NOTES}
              variant="outlined"
              size="small"
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange}
              disabled={isSaving}
              className={classes.field}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Financial Details — add mode only */}
      {!isEdit && (
        <>
          <Typography className={classes.sectionLabel}>Financial Details</Typography>
          <Box className={classes.sectionCard}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="accountBalanceOutsideDF"
                  label={STRINGS.FIELD_ACCT_BAL}
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="number"
                  value={formData.accountBalanceOutsideDF}
                  onChange={handleChange}
                  disabled={isSaving}
                  className={classes.field}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  inputProps={{ step: "0.01", min: 0, inputMode: "decimal" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="existingLimitOutsideDF"
                  label={STRINGS.FIELD_EXISTING_LIMIT}
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="number"
                  value={formData.existingLimitOutsideDF}
                  onChange={handleChange}
                  disabled={isSaving}
                  className={classes.field}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  inputProps={{ step: "0.01", min: 0, inputMode: "decimal" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="proposedLimitOutsideDF"
                  label={STRINGS.FIELD_PROPOSED_LIMIT}
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="number"
                  value={formData.proposedLimitOutsideDF}
                  onChange={handleChange}
                  disabled={isSaving}
                  className={classes.field}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                  inputProps={{ step: "0.01", min: 0, inputMode: "decimal" }}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      {/* Internal Risk Grade — always visible */}
      <Typography className={classes.sectionLabel}>{STRINGS.FIELD_INTERNAL_RISK}</Typography>
      <Box className={classes.sectionCard}>
        <Box className={classes.riskTiles}>
          {Object.entries(RISK_GRADE_MAP).map(([key, meta]) => {
            const isSelected = formData.internalrisk === key;
            return (
              <Box
                key={key}
                component="button"
                type="button"
                className={classes.riskTile}
                disabled={isSaving}
                onClick={() => setFormData((prev) => ({
                  ...prev,
                  internalrisk: prev.internalrisk === key ? "" : key,
                }))}
                style={{
                  color: isSelected ? meta.color : "var(--color-text-secondary)",
                  backgroundColor: isSelected ? meta.bg : "var(--color-background-default)",
                  borderColor: isSelected ? meta.color : "var(--total_row_boldbackground)",
                  opacity: isSaving ? 0.5 : 1,
                }}
              >
                {meta.label}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );

  // Embedded mode: rendered inside EntityMenuCustomForms's dialog — no shell needed
  if (embedded) {
    return (
      <Box>
        {formContent}
        <Box className={classes.embeddedActions}>
          <A_Button
            label="Cancel"
            premiumVariant="secondary"
            disabled={isDisabled}
            onClick={onClose}
          />
          <A_Button
            label={saveLabel}
            premiumVariant="primary"
            disabled={isDisabled}
            onClick={handleSave}
          />
        </Box>
      </Box>
    );
  }

  // Standalone mode: renders its own dialog (used from EGDetail)
  return (
    <A_SimpleDialog
      open={open}
      title={groupName ? `${dialogTitle} - ${groupName}` : dialogTitle}
      maxWidth="md"
      fullWidth
      disableBackdropClick={isDisabled}
      disableEscapeKeyDown={isDisabled}
      onClose={() => { if (!isDisabled) onClose(); }}
      dialogContent={formContent}
      dialogActions={
        <>
          <A_Button
            label="Cancel"
            premiumVariant="secondary"
            disabled={isDisabled}
            onClick={onClose}
          />
          <A_Button
            label={saveLabel}
            premiumVariant="primary"
            disabled={isDisabled}
            onClick={handleSave}
          />
        </>
      }
    />
  );
}

EGEditDialog.propTypes = {
  groupId:   PropTypes.string,
  groupName: PropTypes.string,
  open:      PropTypes.bool.isRequired,
  onClose:   PropTypes.func.isRequired,
  onSaved:   PropTypes.func,
  isEdit:    PropTypes.bool,
  embedded:  PropTypes.bool,
};

EGEditDialog.defaultProps = {
  groupId:   null,
  groupName: "",
  onSaved:   null,
  isEdit:    true,
  embedded:  false,
};

export default EGEditDialog;
